import { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, Plus, Download, Trash2, ExternalLink, Loader2, AlertCircle, CheckCircle, Sparkles, ShieldCheck, Link, ThumbsUp, Star, Pencil, Search, Clock, RefreshCw, Library, Upload, FileUp, X, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from './ui';
import InlineLicenseForm, {
  LicenseFormData,
  DetectedLicense,
  getDefaultLicenseFormData,
  LicenseType
} from './InlineLicenseForm';
import { SharedLiteratureBrowser } from './SharedLiteratureBrowser';
import LiteratureDiscoveryModal from './LiteratureDiscoveryModal';
import CollectionBrowserModal from './CollectionBrowserModal';

// Debounce utility
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

interface Literature {
  id: string;
  title: string;
  description: string;
  literature_type: string;
  original_url: string;
  local_file_path: string | null;
  local_file_size: number | null;
  download_status: string;
  download_error: string | null;
  ai_summary: string | null;
  ai_relevance_score: number | null;
  added_by_ai: boolean;
  display_order: number;
  // New fields for linked literature
  source_type?: 'manual' | 'linked';
  shared_literature_id?: string;
  author?: string;
  publisher?: string;
  publication_year?: number;
  isbn?: string;
  doi?: string;
  // License info (will be fetched separately or included)
  license_type?: string;
  license_confirmed?: boolean;
  // Task #386: felter fra shared_literature for linked literature
  snapshot_status?: string;
  license_category?: string;
  license_label?: string;
  license_url?: string;
  license_source_url?: string;
  owner_member_id?: string;
  discovery_source?: string;
  external_id?: string;
  // Versjonering — settes for linked literature
  linked_version_number?: number;
  shared_current_version?: number;
  has_newer_version?: boolean;
  link_id?: string;
  // Task #393: gruppering under samleside
  parent_link_id?: string | null;
  child_count?: number;
  // Task #395: emne (topic-navn) fra samlesiden
  collection_topic_name?: string | null;
  collection_topic_id?: string | null;
}

export interface LiteratureRelevanceWarning {
  link_id: string;
  shared_literature_id: string;
  title: string;
  author?: string;
  linked_version_number: number;
  score: number;
  reasoning: string;
  severity: 'high' | 'medium';
}

interface LiteratureManagerProps {
  studyPlanId: string;
  readOnly?: boolean;
  // Task #383: onLiteratureChange kan motta ny updated_at slik at StudyPlanForm
  // kan sync-e sin dbUpdatedAt-state og unngå falsk 409-konflikt.
  onLiteratureChange?: (opts?: { updated_at?: string | null }) => void;
  relevanceWarnings?: LiteratureRelevanceWarning[];
  onRelevanceConfirmed?: (linkId: string) => void;
}

export default function LiteratureManager({
  studyPlanId,
  readOnly = false,
  onLiteratureChange,
  relevanceWarnings = [],
  onRelevanceConfirmed
}: LiteratureManagerProps) {
  const { confirm } = useConfirm();
  const [literature, setLiterature] = useState<Literature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Task #387: rad-spesifikk feil-visning nær selve raden (istedenfor kun global feilboks)
  const [rowError, setRowError] = useState<Record<string, string | null>>({});
  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});
  // Task #393: hvilke samlesider (link_id) er ekspandert
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());

  // Add manual modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    description: '',
    literature_type: 'pdf',
    original_url: '',
    author: '',
    publisher: '',
    publication_year: '',
    isbn: '',
    doi: ''
  });

  // Library browser modal
  const [showLibraryBrowser, setShowLibraryBrowser] = useState(false);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [archiveOnAdd, setArchiveOnAdd] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // License form state
  const [licenseForm, setLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [licenseConfirmed, setLicenseConfirmed] = useState(false);
  const [detectedLicense, setDetectedLicense] = useState<DetectedLicense | null>(null);
  const [isDetectingLicense, setIsDetectingLicense] = useState(false);

  // AI suggestions
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  // Task #349-360 (2026-07-07): ny litteratur-oppdagelses-modal med lisens-badges
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false);
  // Fase 4G: "Bla og velg"-modal for NDLA-samlesider
  const [browsingCollectionLinkId, setBrowsingCollectionLinkId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [_loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [disclaimer, setDisclaimer] = useState<string>('');

  // Edit AI suggestion before adding
  const [editingSuggestion, setEditingSuggestion] = useState<any | null>(null);
  const [suggestionEditForm, setSuggestionEditForm] = useState({
    title: '',
    description: '',
    url: '',
    literature_type: 'webpage',
    author: '',
    publisher: ''
  });
  const [suggestionLicenseForm, setSuggestionLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [suggestionLicenseConfirmed, setSuggestionLicenseConfirmed] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLit, setEditingLit] = useState<Literature | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    literature_type: 'pdf',
    original_url: '',
    author: '',
    publisher: '',
    publication_year: '',
    isbn: '',
    doi: '',
    archive_locally: false
  });
  const [editLicenseForm, setEditLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [editLicenseConfirmed, setEditLicenseConfirmed] = useState(false);
  const [showLicenseInEdit, setShowLicenseInEdit] = useState(false);

  // URL Correction modal
  const [showUrlCorrectionModal, setShowUrlCorrectionModal] = useState(false);
  const [urlCorrectionLit, setUrlCorrectionLit] = useState<Literature | null>(null);
  const [urlSuggestions, setUrlSuggestions] = useState<any[]>([]);
  const [loadingUrlSuggestions, setLoadingUrlSuggestions] = useState(false);
  const [applyingUrl, setApplyingUrl] = useState<string | null>(null);

  useEffect(() => {
    loadLiterature();
  }, [studyPlanId]);

  // Debounced license detection from URL
  const detectLicenseFromUrl = useCallback(
    debounce(async (url: string) => {
      if (!url || !url.startsWith('http')) {
        setDetectedLicense(null);
        return;
      }

      setIsDetectingLicense(true);
      try {
        const response = await authenticatedFetch('/api/literature/detect-license', {
          method: 'POST',
          body: JSON.stringify({ url })
        });
        const data = await response.json();

        if (data.success && data.detected) {
          setDetectedLicense(data);
        } else {
          setDetectedLicense(null);
        }
      } catch (err) {
        console.error('License detection failed:', err);
        setDetectedLicense(null);
      } finally {
        setIsDetectingLicense(false);
      }
    }, 500),
    []
  );

  // Trigger license detection when URL changes
  useEffect(() => {
    if (showAddModal && addForm.original_url) {
      detectLicenseFromUrl(addForm.original_url);
    }
  }, [addForm.original_url, showAddModal]);

  // Reset license form when modal closes
  useEffect(() => {
    if (!showAddModal) {
      setLicenseForm(getDefaultLicenseFormData());
      setLicenseConfirmed(false);
      setDetectedLicense(null);
      setUploadedFile(null);
      setUploadProgress(null);
    }
  }, [showAddModal]);

  // File upload handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/html', 'text/plain'];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.html', '.htm', '.txt'];

    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      setError('Ugyldig filtype. Støttede formater: PDF, Word, HTML, TXT');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('Filen er for stor. Maks størrelse er 50 MB.');
      return;
    }

    setUploadedFile(file);
    setAddForm(prev => ({
      ...prev,
      title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
      literature_type: file.type.includes('pdf') ? 'pdf' :
                       file.type.includes('word') ? 'word' :
                       file.type.includes('html') ? 'webpage' : 'other',
      original_url: '' // Clear URL when file is selected
    }));
    setError('');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle library selection
  const handleLibrarySelect = (lit: any) => {
    setShowLibraryBrowser(false);
    loadLiterature(); // Refresh list
    setSuccess(`"${lit.title}" ble lagt til i studieplanen`);
    if (onLiteratureChange) {
      onLiteratureChange();
    }
  };

  const loadLiterature = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`);
      const data = await response.json();

      if (data.success) {
        setLiterature(data.literature);
      } else {
        setError(data.message || 'Kunne ikke laste litteratur');
      }
    } catch (err) {
      console.error('Error loading literature:', err);
      setError('Kunne ikke laste litteratur');
    } finally {
      setLoading(false);
    }
  };

  const handleAddManual = async () => {
    // Validate: either file or URL must be provided
    if (!addForm.title) {
      setError('Tittel er påkrevd');
      return;
    }

    if (!uploadedFile && !addForm.original_url) {
      setError('Du må enten laste opp en fil eller oppgi en URL');
      return;
    }

    if (!licenseConfirmed) {
      setError('Du må bekrefte lisensinformasjonen før du kan legge til litteratur');
      return;
    }

    if (licenseForm.license_type === 'unknown') {
      setError('Du må velge en lisenstype');
      return;
    }

    if (licenseForm.license_type === 'creative_commons' && !licenseForm.cc_variant) {
      setError('Du må velge en CC-variant for Creative Commons-lisenser');
      return;
    }

    try {
      let response;

      if (uploadedFile) {
        // File upload mode
        setUploadProgress(0);
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('title', addForm.title);
        formData.append('description', addForm.description);
        formData.append('literature_type', addForm.literature_type);
        formData.append('license_type', licenseForm.license_type);
        if (licenseForm.cc_variant) formData.append('cc_variant', licenseForm.cc_variant);
        formData.append('allows_distribution', String(licenseForm.allows_distribution));
        formData.append('allows_modification', String(licenseForm.allows_modification));
        formData.append('allows_commercial_use', String(licenseForm.allows_commercial_use));
        formData.append('requires_attribution', String(licenseForm.requires_attribution));
        if (licenseForm.attribution_text) formData.append('attribution_text', licenseForm.attribution_text);
        if (licenseForm.rights_holder) formData.append('rights_holder', licenseForm.rights_holder);
        if (licenseForm.license_url) formData.append('license_url', licenseForm.license_url);
        if (licenseForm.valid_until) formData.append('valid_until', licenseForm.valid_until);
        formData.append('is_perpetual', String(licenseForm.is_perpetual));
        formData.append('is_organization_specific', String(licenseForm.is_organization_specific));
        if (licenseForm.max_users) formData.append('max_users', String(licenseForm.max_users));
        if (licenseForm.usage_notes) formData.append('usage_notes', licenseForm.usage_notes);
        formData.append('license_confirmed', 'true');

        // Use XMLHttpRequest for progress tracking
        response = await new Promise<Response>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `/api/study-plans/${studyPlanId}/literature/upload`);

          // Get auth token
          const authData = localStorage.getItem('auth');
          if (authData) {
            const { auth_token } = JSON.parse(authData);
            if (auth_token) {
              xhr.setRequestHeader('Authorization', `Bearer ${auth_token}`);
            }
          }

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setUploadProgress(Math.round((e.loaded / e.total) * 100));
            }
          };

          xhr.onload = () => {
            const response = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText
            });
            resolve(response);
          };

          xhr.onerror = () => reject(new Error('Upload failed'));
          xhr.send(formData);
        });
      } else {
        // URL mode
        response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: addForm.title,
            description: addForm.description,
            literature_type: addForm.literature_type,
            original_url: addForm.original_url,
            author: addForm.author || null,
            publisher: addForm.publisher || null,
            publication_year: addForm.publication_year ? parseInt(addForm.publication_year) : null,
            isbn: addForm.isbn || null,
            doi: addForm.doi || null,
            archive_on_add: archiveOnAdd,
            // License fields
            license_type: licenseForm.license_type,
            cc_variant: licenseForm.cc_variant,
            allows_distribution: licenseForm.allows_distribution,
            allows_modification: licenseForm.allows_modification,
            allows_commercial_use: licenseForm.allows_commercial_use,
            requires_attribution: licenseForm.requires_attribution,
            attribution_text: licenseForm.attribution_text,
            rights_holder: licenseForm.rights_holder,
            license_url: licenseForm.license_url,
            valid_until: licenseForm.valid_until,
            is_perpetual: licenseForm.is_perpetual,
            is_organization_specific: licenseForm.is_organization_specific,
            max_users: licenseForm.max_users,
            usage_notes: licenseForm.usage_notes,
            license_confirmed: true
          })
        });
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(uploadedFile ? 'Fil lastet opp og lagt til' : 'Litteratur lagt til med lisensbekreftelse');
        setShowAddModal(false);
        setAddForm({ title: '', description: '', literature_type: 'pdf', original_url: '', author: '', publisher: '', publication_year: '', isbn: '', doi: '' });
        setLicenseForm(getDefaultLicenseFormData());
        setLicenseConfirmed(false);
        setUploadedFile(null);
        setUploadProgress(null);
        loadLiterature();
        onLiteratureChange?.();
      } else {
        setError(data.message || 'Kunne ikke legge til litteratur');
      }
    } catch (err) {
      setError('Kunne ikke legge til litteratur');
      setUploadProgress(null);
    }
  };

  // @ts-expect-error — beholder for legacy-fallback, task #349-360
  const _handleAISuggest = async () => {
    try {
      setLoadingSuggestions(true);
      setError('');

      const response = await authenticatedFetch('/api/ai/suggest-literature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ study_plan_id: studyPlanId, count: 5 })
      });

      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions);
        setDisclaimer(data.disclaimer || '');
        setShowSuggestionsModal(true);
      } else {
        setError('Kunne ikke generere forslag');
      }
    } catch (err) {
      setError('Kunne ikke generere forslag');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: any) => {
    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestion.title,
          description: suggestion.summary,
          literature_type: suggestion.literature_type,
          original_url: suggestion.url,
          added_by_ai: true,
          ai_relevance_score: suggestion.relevance_score
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Litteratur lagt til fra KI-forslag');
        loadLiterature();
        setSuggestions(suggestions.filter(s => s !== suggestion));
        onLiteratureChange?.();
      }
    } catch (err) {
      setError('Kunne ikke legge til litteratur');
    }
  };

  const handleApproveAndAddToCurated = async (suggestion: any) => {
    try {
      // Use the new request flow - adds to study plan AND sends request to admin
      const response = await authenticatedFetch('/api/curated-sources/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestion.title,
          url: suggestion.url,
          literature_type: suggestion.literature_type,
          description: suggestion.summary,
          study_plan_id: studyPlanId,
          ai_relevance_score: suggestion.relevance_score
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', response.status, errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.error || errorData.message || `Feil ${response.status}`);
        } catch {
          setError(`Serverfeil: ${response.status}`);
        }
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message || 'Kilde lagt til i studieplanen. Forespørsel sendt til studieforbundet.');
        loadLiterature();
        setSuggestions(suggestions.filter(s => s !== suggestion));
        onLiteratureChange?.();
      } else {
        setError(data.error || 'Kunne ikke legge til kilde');
      }
    } catch (err: any) {
      console.error('handleApproveAndAddToCurated error:', err);
      setError(err.message || 'Kunne ikke legge til kilde');
    }
  };

  // Open suggestion for editing before adding
  const handleEditSuggestion = (suggestion: any) => {
    setEditingSuggestion(suggestion);
    setSuggestionEditForm({
      title: suggestion.title || '',
      description: suggestion.summary || '',
      url: suggestion.url || '',
      literature_type: suggestion.literature_type || 'webpage',
      author: suggestion.author || '',
      publisher: suggestion.publisher || ''
    });
    setSuggestionLicenseForm(getDefaultLicenseFormData());
    setSuggestionLicenseConfirmed(false);
  };

  // Add edited suggestion
  const handleAddEditedSuggestion = async () => {
    if (!suggestionEditForm.title || !suggestionEditForm.url) {
      setError('Tittel og URL er påkrevd');
      return;
    }

    if (!suggestionLicenseConfirmed) {
      setError('Du må bekrefte lisensinformasjonen');
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestionEditForm.title,
          description: suggestionEditForm.description,
          literature_type: suggestionEditForm.literature_type,
          original_url: suggestionEditForm.url,
          author: suggestionEditForm.author,
          publisher: suggestionEditForm.publisher,
          added_by_ai: true,
          ai_relevance_score: editingSuggestion?.relevance_score,
          // License fields
          license_type: suggestionLicenseForm.license_type,
          cc_variant: suggestionLicenseForm.cc_variant,
          allows_distribution: suggestionLicenseForm.allows_distribution,
          allows_modification: suggestionLicenseForm.allows_modification,
          allows_commercial_use: suggestionLicenseForm.allows_commercial_use,
          requires_attribution: suggestionLicenseForm.requires_attribution,
          attribution_text: suggestionLicenseForm.attribution_text,
          rights_holder: suggestionLicenseForm.rights_holder,
          license_confirmed: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('KI-forslag redigert og lagt til');
        loadLiterature();
        setSuggestions(suggestions.filter(s => s !== editingSuggestion));
        setEditingSuggestion(null);
        onLiteratureChange?.();
      } else {
        setError(data.message || 'Kunne ikke legge til litteratur');
      }
    } catch (err) {
      setError('Kunne ikke legge til litteratur');
    }
  };

  const handleDownload = async (litId: string) => {
    // Task #387: rad-spesifikk feilstate slik at brukeren ser hva som skjedde
    setRowError(prev => ({ ...prev, [litId]: null }));
    setRowLoading(prev => ({ ...prev, [litId]: true }));
    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${litId}/download`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message || 'Fil lastet ned og lagret');
        loadLiterature();
        onLiteratureChange?.();
      } else {
        setRowError(prev => ({ ...prev, [litId]: data.message || 'Nedlasting feilet' }));
      }
    } catch (err) {
      setRowError(prev => ({ ...prev, [litId]: 'Nedlasting feilet (nettverksfeil)' }));
    } finally {
      setRowLoading(prev => ({ ...prev, [litId]: false }));
    }
  };

  const handleDelete = async (litId: string) => {
    const confirmed = await confirm({
      title: 'Fjern litteratur',
      message: 'Er du sikker på at du vil fjerne denne litteraturen?',
      confirmLabel: 'Fjern',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${litId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Litteratur fjernet');
        loadLiterature();
        onLiteratureChange?.();
      }
    } catch (err) {
      setError('Kunne ikke fjerne litteratur');
    }
  };

  const handleEdit = (lit: Literature) => {
    setEditingLit(lit);
    setEditForm({
      title: lit.title,
      description: lit.description || '',
      literature_type: lit.literature_type,
      original_url: lit.original_url,
      author: lit.author || '',
      publisher: lit.publisher || '',
      publication_year: lit.publication_year?.toString() || '',
      isbn: lit.isbn || '',
      doi: lit.doi || '',
      archive_locally: lit.download_status === 'downloaded'
    });
    // Set existing license info if available.
    // Task #387: for delt litteratur mangler lit.license_type men vi har
    // lit.license_category (free/open_access/bokhylla/copyrighted/commercial).
    // Map slik at UI viser noe fornuftig.
    const mapCategoryToLicenseType = (cat?: string): LicenseType | null => {
      if (!cat) return null;
      if (cat === 'free') return 'creative_commons';
      if (cat === 'open_access') return 'open_access';
      if (cat === 'bokhylla') return 'proprietary';
      if (cat === 'copyrighted' || cat === 'commercial') return 'proprietary';
      return null;
    };
    const derivedType = (lit.license_type as LicenseType) || mapCategoryToLicenseType(lit.license_category);
    if (derivedType) {
      setEditLicenseForm({
        ...getDefaultLicenseFormData(),
        license_type: derivedType,
        license_url: (lit as any).license_source_url || '',
      });
      // Task #387: delt litteratur fra bibliotek regnes som allerede bekreftet
      setEditLicenseConfirmed(lit.license_confirmed || lit.source_type === 'linked');
      setShowLicenseInEdit(false);
    } else {
      setEditLicenseForm(getDefaultLicenseFormData());
      setEditLicenseConfirmed(false);
      setShowLicenseInEdit(false);
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingLit || !editForm.title || !editForm.original_url) {
      setError('Tittel og URL er påkrevd');
      return;
    }

    // If editing license, require confirmation
    if (showLicenseInEdit && !editLicenseConfirmed) {
      setError('Du må bekrefte lisensinformasjonen');
      return;
    }

    try {
      const payload: any = { ...editForm };

      // Include license info if editing license
      if (showLicenseInEdit) {
        payload.license_type = editLicenseForm.license_type;
        payload.cc_variant = editLicenseForm.cc_variant;
        payload.allows_distribution = editLicenseForm.allows_distribution;
        payload.allows_modification = editLicenseForm.allows_modification;
        payload.allows_commercial_use = editLicenseForm.allows_commercial_use;
        payload.requires_attribution = editLicenseForm.requires_attribution;
        payload.attribution_text = editLicenseForm.attribution_text;
        payload.rights_holder = editLicenseForm.rights_holder;
        payload.license_url = editLicenseForm.license_url;
        payload.valid_until = editLicenseForm.valid_until;
        payload.is_perpetual = editLicenseForm.is_perpetual;
        payload.is_organization_specific = editLicenseForm.is_organization_specific;
        payload.max_users = editLicenseForm.max_users;
        payload.usage_notes = editLicenseForm.usage_notes;
        payload.license_confirmed = true;
      }

      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${editingLit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        // Task #387: hvis brukeren haket "Arkiver lokalt" og ressursen ikke
        // allerede er arkivert, trigg download i samme operasjon.
        const wasNotArchived = !['downloaded','auto_snapshot','user_uploaded'].includes(editingLit.snapshot_status || editingLit.download_status || '');
        const isCollection = /^ndla-(subject|topic)-/.test(editingLit.external_id || '');
        if (editForm.archive_locally && wasNotArchived && !isCollection) {
          const dl = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/${editingLit.id}/download`, { method: 'POST' });
          const dlData = await dl.json();
          if (dlData.success) {
            setSuccess('Litteratur oppdatert og arkivert lokalt');
          } else {
            setSuccess('Litteratur oppdatert, men arkivering feilet: ' + (dlData.message || 'ukjent'));
          }
        } else {
          setSuccess('Litteratur oppdatert');
        }
        setShowEditModal(false);
        setEditingLit(null);
        setShowLicenseInEdit(false);
        loadLiterature();
        onLiteratureChange?.();
      } else {
        setError(data.message || 'Kunne ikke oppdatere litteratur');
      }
    } catch (err) {
      setError('Kunne ikke oppdatere litteratur');
    }
  };

  // Find alternative URL for broken links
  const handleFindAlternativeUrl = async (lit: Literature) => {
    setUrlCorrectionLit(lit);
    setUrlSuggestions([]);
    setShowUrlCorrectionModal(true);
    setLoadingUrlSuggestions(true);
    setError('');

    try {
      const response = await authenticatedFetch(`/api/literature/${lit.id}/find-alternative`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.success) {
        setUrlSuggestions(data.suggestions || []);
        if (data.suggestions?.length === 0) {
          setError('Ingen alternative URLer funnet');
        }
      } else {
        setError(data.message || 'Kunne ikke søke etter alternativ URL');
      }
    } catch (err) {
      setError('Kunne ikke søke etter alternativ URL');
    } finally {
      setLoadingUrlSuggestions(false);
    }
  };

  // Apply corrected URL
  const handleApplyUrlCorrection = async (newUrl: string, method: string) => {
    if (!urlCorrectionLit) return;

    setApplyingUrl(newUrl);
    setError('');

    try {
      const response = await authenticatedFetch(`/api/literature/${urlCorrectionLit.id}/correct-url`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_url: newUrl,
          method: method,
          table: 'study_plan'
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('URL oppdatert! Kilden kan nå lastes ned på nytt.');
        setShowUrlCorrectionModal(false);
        setUrlCorrectionLit(null);
        setUrlSuggestions([]);
        loadLiterature();
        // Note: We don't call onLiteratureChange here because URL corrections
        // should not trigger revalidation (as per user requirement)
      } else {
        setError(data.message || 'Kunne ikke oppdatere URL');
      }
    } catch (err) {
      setError('Kunne ikke oppdatere URL');
    } finally {
      setApplyingUrl(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  const handleConfirmRelevance = async (warning: LiteratureRelevanceWarning) => {
    try {
      const note = window.prompt(
        `Bekreft relevans for "${warning.title}".\n\n` +
        `KI-vurdering: ${warning.reasoning}\n\n` +
        `Skriv gjerne en kort begrunnelse for hvorfor du mener litteraturen er relevant (valgfritt):`,
        ''
      );
      // null = avbrutt, tom streng = bekreftet uten notat
      if (note === null) return;

      const res = await authenticatedFetch(
        `/api/study-plans/${studyPlanId}/literature/${warning.link_id}/confirm-relevance`,
        {
          method: 'POST',
          body: JSON.stringify({
            note: note || null,
            llm_score: warning.score,
            llm_reasoning: warning.reasoning,
          }),
        }
      );
      if (!res.ok) {
        alert('Kunne ikke bekrefte relevans. Prøv igjen.');
        return;
      }
      onRelevanceConfirmed?.(warning.link_id);
    } catch (e) {
      console.error('Confirm relevance error:', e);
      alert('Feil ved bekreftelse av relevans.');
    }
  };

  return (
    <div className="space-y-4">
      {/* Litteratur-relevans-varsel (task #346, 2026-07-07) — vises over header */}
      {relevanceWarnings.length > 0 && (
        <div className="border rounded-lg p-4 bg-kf-orange-50 border-kf-orange-300">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-kf-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-3">
              <div>
                <h4 className="font-semibold text-kf-orange-900">
                  KI-vurdering: {relevanceWarnings.length} litteratur-referanse{relevanceWarnings.length > 1 ? 'r' : ''} kan være mindre relevant
                </h4>
                <p className="text-sm text-kf-orange-800 mt-1">
                  Vurder om referansene faktisk hører til studieplanens fagområde. Hvis du er trygg på at de er relevante, klikk «Bekreft relevans» — da vises varselet ikke igjen.
                </p>
              </div>
              {relevanceWarnings.map(w => (
                <div key={w.link_id} className="bg-white border border-kf-orange-200 rounded p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {w.title}
                        {w.author && <span className="text-gray-500 font-normal"> — {w.author}</span>}
                      </div>
                      <div className="text-xs mt-1 flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded ${
                          w.severity === 'high'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          KI-score: {w.score}/5
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 mt-2 italic">
                        {w.reasoning}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleConfirmRelevance(w)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Bekreft relevans
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-kf-primary" />
          Litteraturliste
        </h3>

        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            {/* Task #349-360 (2026-07-07): ny Søk-etter-litteratur-knapp
                med live-søk mot NDLA/NB/Crossref. Gammel "KI-forslag"-flyt
                (statisk pool + LLM-omformulering) fjernet. */}
            <button
              type="button"
              onClick={() => setShowDiscoveryModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm"
            >
              <Sparkles className="h-4 w-4" />
              KI-litteratursøk
            </button>

            <button
              type="button"
              onClick={() => setShowLibraryBrowser(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm"
            >
              <Library className="h-4 w-4" />
              Velg fra bibliotek
            </button>

            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary flex items-center gap-2 text-sm"
            >
              <Plus className="h-4 w-4" />
              Legg til manuelt
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border-l-4 border-green-400 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {literature.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Ingen litteratur lagt til ennå</p>
          <p className="text-sm text-gray-500 mt-1">
            Støttede formater: PDF, Word, HTML (kun dokumenter, maks 50 MB)
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Task #393+395: samleside først, barn nested + gruppert per emne */}
          {(() => {
            const topLevel = literature.filter(l => !l.parent_link_id);
            const childrenByParent = new Map<string, Literature[]>();
            for (const lit of literature) {
              if (lit.parent_link_id) {
                const arr = childrenByParent.get(lit.parent_link_id) || [];
                arr.push(lit);
                childrenByParent.set(lit.parent_link_id, arr);
              }
            }
            type Row = { kind: 'lit'; data: Literature } | { kind: 'topic'; name: string; count: number };
            const rows: Row[] = [];
            for (const parent of topLevel) {
              rows.push({ kind: 'lit', data: parent });
              if (parent.link_id && expandedCollections.has(parent.link_id)) {
                const kids = childrenByParent.get(parent.link_id) || [];
                // Task #395: gruppér per collection_topic_name, med "Uten emne" fallback
                const byTopic = new Map<string, Literature[]>();
                for (const kid of kids) {
                  const t = kid.collection_topic_name || 'Uten emne';
                  const arr = byTopic.get(t) || [];
                  arr.push(kid);
                  byTopic.set(t, arr);
                }
                const topicNames = Array.from(byTopic.keys()).sort();
                for (const topic of topicNames) {
                  const items = byTopic.get(topic)!;
                  rows.push({ kind: 'topic', name: topic, count: items.length });
                  for (const item of items) rows.push({ kind: 'lit', data: item });
                }
              }
            }
            return rows.map((row) => {
              if (row.kind === 'topic') {
                return (
                  <div key={`topic-${row.name}`} className="ml-8 mt-3 mb-1 px-3 py-1.5 bg-kf-light border-l-4 border-l-kf-accent rounded flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-kf-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-kf-primary">{row.name}</span>
                    <span className="text-xs text-kf-primary-60">({row.count} artikler)</span>
                  </div>
                );
              }
              const lit = row.data;
              return (
            <div
              key={lit.id}
              className={`p-4 bg-white border rounded-lg hover:shadow-md transition ${lit.parent_link_id ? 'ml-8 border-l-4 border-l-kf-primary-40' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start gap-2 flex-wrap">
                    {/* Task #393: ekspansjons-knapp på samleside-rader */}
                    {!lit.parent_link_id && /^ndla-(subject|topic)-/.test(lit.external_id || '') && lit.link_id && (
                      <button
                        type="button"
                        onClick={() => {
                          const id = lit.link_id!;
                          setExpandedCollections(prev => {
                            const n = new Set(prev);
                            if (n.has(id)) n.delete(id); else n.add(id);
                            return n;
                          });
                        }}
                        className="p-1 text-kf-primary hover:bg-kf-primary-10 rounded flex-shrink-0"
                        title={expandedCollections.has(lit.link_id!) ? 'Skjul artikler' : `Vis artikler under samlesiden${lit.child_count ? ` (${lit.child_count})` : ''}`}
                      >
                        {expandedCollections.has(lit.link_id!)
                          ? <ChevronDown className="h-5 w-5" />
                          : <ChevronRight className="h-5 w-5" />}
                      </button>
                    )}
                    <h4 className="font-semibold text-gray-900 min-w-0">{lit.title}</h4>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {/* Source type badge */}
                      {lit.source_type === 'linked' && (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <Library className="h-3 w-3" />
                          Fra bibliotek
                        </span>
                      )}
                      {/* Task #393: badge for samleside med antall artikler */}
                      {!lit.parent_link_id && /^ndla-(subject|topic)-/.test(lit.external_id || '') && (lit.child_count || 0) > 0 && (
                        <span className="px-2 py-0.5 bg-kf-accent-20 text-kf-primary text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <BookOpen className="h-3 w-3" />
                          {lit.child_count} pensum-artikler
                        </span>
                      )}
                      {lit.added_by_ai && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <Sparkles className="h-3 w-3" />
                          KI-forslag
                        </span>
                      )}
                      {/* Task #393: sjekk både snapshot_status (ny modell) og download_status (legacy) */}
                      {['downloaded','auto_snapshot','user_uploaded'].includes(lit.snapshot_status || lit.download_status || '') && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <CheckCircle className="h-3 w-3" />
                          Lagret lokalt
                        </span>
                      )}
                      {/* Task #393: "Arkiverer..."-badge for bakgrunn-snapshot i gang */}
                      {lit.snapshot_status === 'pending' && lit.parent_link_id && (
                        <span className="px-2 py-0.5 bg-kf-blue-100 text-kf-blue-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Arkiverer...
                        </span>
                      )}
                      {lit.license_type && lit.license_type !== 'unknown' && (
                        <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 whitespace-nowrap ${
                          lit.license_type === 'public_domain' ? 'bg-green-100 text-green-700' :
                          lit.license_type === 'creative_commons' ? 'bg-kf-blue-100 text-kf-blue-700' :
                          lit.license_type === 'open_access' ? 'bg-teal-100 text-teal-700' :
                          lit.license_type === 'citation_right' ? 'bg-indigo-100 text-indigo-700' :
                          lit.license_type === 'kopinor_agreement' ? 'bg-kf-orange-100 text-kf-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          <ShieldCheck className="h-3 w-3" />
                          {lit.license_type === 'public_domain' ? 'Offentlig domene' :
                           lit.license_type === 'creative_commons' ? 'CC-lisens' :
                           lit.license_type === 'open_access' ? 'Åpen tilgang' :
                           lit.license_type === 'citation_right' ? 'Sitatrett' :
                           lit.license_type === 'kopinor_agreement' ? 'Kopinor' :
                           lit.license_type === 'proprietary' ? 'Proprietær' :
                           lit.license_type === 'subscription' ? 'Abonnement' :
                           lit.license_type === 'purchased' ? 'Kjøpt' :
                           lit.license_type === 'educational' ? 'Utdanning' :
                           lit.license_type}
                        </span>
                      )}
                      {lit.license_confirmed && !lit.license_type && (
                        <span className="px-2 py-0.5 bg-kf-blue-100 text-kf-blue-700 text-xs rounded-full flex items-center gap-1 whitespace-nowrap">
                          <ShieldCheck className="h-3 w-3" />
                          Lisens bekreftet
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Author and publisher info */}
                  {(lit.author || lit.publisher) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {lit.author && <span>{lit.author}</span>}
                      {lit.author && lit.publisher && <span> • </span>}
                      {lit.publisher && <span>{lit.publisher}</span>}
                      {lit.publication_year && <span> ({lit.publication_year})</span>}
                    </p>
                  )}

                  {lit.description && (
                    <p className="text-sm text-gray-600 mt-1">{lit.description}</p>
                  )}

                  {/* Versjon-varsel for linked litteratur når en nyere versjon finnes */}
                  {lit.source_type === 'linked' && lit.has_newer_version && lit.shared_literature_id && lit.link_id && (
                    <NewVersionBanner
                      sharedLiteratureId={lit.shared_literature_id}
                      linkId={lit.link_id}
                      currentVersion={lit.linked_version_number || 1}
                      latestVersion={lit.shared_current_version || 1}
                      onUpgraded={() => loadLiterature()}
                    />
                  )}

                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                    <span className="capitalize">{lit.literature_type}</span>
                    {lit.local_file_size && (
                      <span>{Math.round(lit.local_file_size / 1024)} KB</span>
                    )}
                    {lit.ai_relevance_score && (
                      <span>Relevans: {lit.ai_relevance_score}%</span>
                    )}
                    {lit.isbn && <span>ISBN: {lit.isbn}</span>}
                    {lit.doi && <span>DOI: {lit.doi}</span>}
                  </div>

                  {lit.download_status === 'failed' && lit.download_error && (
                    <div className="mt-2">
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {lit.download_error}
                      </p>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => handleFindAlternativeUrl(lit)}
                          className="mt-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 text-sm flex items-center gap-2"
                        >
                          <Search className="h-4 w-4" />
                          Finn alternativ URL
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <a
                    href={lit.original_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-600 hover:text-kf-primary hover:bg-gray-100 rounded-lg transition"
                    title="Åpne original URL"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleEdit(lit)}
                      className="p-2 text-gray-600 hover:text-kf-primary hover:bg-gray-100 rounded-lg transition"
                      title="Rediger litteratur"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}

                  {/* Fase 4G: for NDLA-samlesider (subject/topic), tilby "Bla og velg" istedenfor download */}
                  {!readOnly && /^ndla-(subject|topic)-/.test(lit.external_id || '') && lit.link_id && (
                    <button
                      type="button"
                      onClick={() => setBrowsingCollectionLinkId(lit.link_id!)}
                      className="p-2 text-gray-600 hover:text-kf-primary hover:bg-kf-primary-10 rounded-lg transition"
                      title="Bla i innhold og velg pensum-artikler"
                    >
                      <Library className="h-4 w-4" />
                    </button>
                  )}

                  {!readOnly && !['downloaded','auto_snapshot','user_uploaded'].includes(lit.snapshot_status || lit.download_status || '') && !/^ndla-(subject|topic)-/.test(lit.external_id || '') && (
                    <button
                      type="button"
                      onClick={() => handleDownload(lit.id)}
                      disabled={rowLoading[lit.id]}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition disabled:opacity-50"
                      title="Last ned og lagre lokalt"
                    >
                      {rowLoading[lit.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </button>
                  )}

                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleDelete(lit.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Fjern litteratur"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              {/* Task #387: rad-spesifikk feilmelding vises tett på selve raden */}
              {rowError[lit.id] && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800">{rowError[lit.id]}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRowError(prev => ({ ...prev, [lit.id]: null }))}
                    className="text-red-600 hover:text-red-800"
                    title="Lukk"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          );
          });
          })()}
        </div>
      )}

      {/* Add Manual Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4">Legg til litteratur manuelt</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tittel *
                </label>
                <input
                  type="text"
                  value={addForm.title}
                  onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  placeholder="F.eks. 'Introduksjon til pedagogikk'"
                />
              </div>

              {/* File Upload or URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fil eller URL *
                </label>

                {/* File Upload Area */}
                {!uploadedFile && !addForm.original_url && (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                      isDragging
                        ? 'border-kf-primary bg-kf-primary/5'
                        : 'border-gray-300 hover:border-kf-primary hover:bg-gray-50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.html,.htm,.txt"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-kf-primary' : 'text-gray-400'}`} />
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-kf-primary">Klikk for å laste opp</span>
                      {' '}eller dra og slipp fil her
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      PDF, Word, HTML (maks 50 MB)
                    </p>
                  </div>
                )}

                {/* Uploaded File Preview */}
                {uploadedFile && (
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileUp className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearUploadedFile}
                      className="p-1 hover:bg-green-100 rounded-full"
                    >
                      <X className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress !== null && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>Laster opp...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-kf-primary transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Divider */}
                {!uploadedFile && (
                  <div className="flex items-center gap-4 my-4">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="text-sm text-gray-500">eller</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>
                )}

                {/* URL Input */}
                {!uploadedFile && (
                  <div>
                    <input
                      type="url"
                      value={addForm.original_url}
                      onChange={(e) => setAddForm({ ...addForm, original_url: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                      placeholder="https://example.com/document.pdf"
                    />

                    {/* Archive checkbox for URL */}
                    {addForm.original_url && (
                      <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={archiveOnAdd}
                          onChange={(e) => setArchiveOnAdd(e.target.checked)}
                          className="rounded border-gray-300 text-kf-primary focus:ring-kf-primary"
                        />
                        <span className="text-sm text-gray-700">
                          Last ned og lagre lokalt (anbefalt for permanent tilgang)
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>

              {/* Author and Publisher */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forfatter
                  </label>
                  <input
                    type="text"
                    value={addForm.author}
                    onChange={(e) => setAddForm({ ...addForm, author: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Ola Nordmann"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgiver
                  </label>
                  <input
                    type="text"
                    value={addForm.publisher}
                    onChange={(e) => setAddForm({ ...addForm, publisher: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Universitetsforlaget"
                  />
                </div>
              </div>

              {/* Publication details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgivelsesår
                  </label>
                  <input
                    type="number"
                    value={addForm.publication_year}
                    onChange={(e) => setAddForm({ ...addForm, publication_year: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. 2024"
                    min="1900"
                    max="2100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={addForm.isbn}
                    onChange={(e) => setAddForm({ ...addForm, isbn: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="978-82-..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DOI
                  </label>
                  <input
                    type="text"
                    value={addForm.doi}
                    onChange={(e) => setAddForm({ ...addForm, doi: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="10.1000/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={addForm.literature_type}
                  onChange={(e) => setAddForm({ ...addForm, literature_type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                >
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="webpage">Nettside</option>
                  <option value="book">Bok</option>
                  <option value="article">Artikkel</option>
                  <option value="video">Video</option>
                  <option value="manual">Manual/Instruksjon</option>
                  <option value="other">Annet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <textarea
                  value={addForm.description}
                  onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  rows={3}
                  placeholder="Kort beskrivelse av innholdet..."
                />
              </div>

              {/* License Form - Required before adding */}
              <InlineLicenseForm
                value={licenseForm}
                onChange={setLicenseForm}
                detectedLicense={detectedLicense}
                isDetecting={isDetectingLicense}
                confirmed={licenseConfirmed}
                onConfirmChange={setLicenseConfirmed}
              />
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleAddManual}
                disabled={!licenseConfirmed}
                className={`px-4 py-2 rounded-lg ${
                  licenseConfirmed
                    ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Legg til
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestions Modal */}
      {showSuggestionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              KI-forslag på litteratur
            </h3>

            {/* Disclaimer */}
            {disclaimer && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800 font-medium">{disclaimer}</p>
                </div>
              </div>
            )}

            {suggestions.length === 0 ? (
              <p className="text-gray-600 text-center py-8">Ingen forslag funnet</p>
            ) : (
              <div className="space-y-4">
                {suggestions.map((suggestion, index) => (
                  <div key={index} className={`p-4 rounded-lg ${editingSuggestion === suggestion ? 'bg-kf-blue-50 border-2 border-kf-blue-300' : 'bg-purple-50 border border-purple-200'}`}>
                    {/* Show edit form inline if this suggestion is being edited */}
                    {editingSuggestion === suggestion ? (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Pencil className="h-5 w-5 text-kf-blue-600" />
                          Rediger forslag før tillegg
                        </h4>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                            <input
                              type="text"
                              value={suggestionEditForm.title}
                              onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, title: e.target.value })}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Forfatter</label>
                              <input
                                type="text"
                                value={suggestionEditForm.author}
                                onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, author: e.target.value })}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                                placeholder="F.eks. NDLA"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Utgiver</label>
                              <input
                                type="text"
                                value={suggestionEditForm.publisher}
                                onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, publisher: e.target.value })}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                                placeholder="F.eks. Utdanningsdirektoratet"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                            <input
                              type="url"
                              value={suggestionEditForm.url}
                              onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, url: e.target.value })}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                              <select
                                value={suggestionEditForm.literature_type}
                                onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, literature_type: e.target.value })}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                              >
                                <option value="pdf">PDF</option>
                                <option value="webpage">Nettside</option>
                                <option value="book">Bok</option>
                                <option value="article">Artikkel</option>
                                <option value="video">Video</option>
                                <option value="other">Annet</option>
                              </select>
                            </div>
                            <div className="flex items-end">
                              <p className="text-sm text-gray-500">
                                Relevans: {suggestion.relevance_score}%
                              </p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                            <textarea
                              value={suggestionEditForm.description}
                              onChange={(e) => setSuggestionEditForm({ ...suggestionEditForm, description: e.target.value })}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent bg-white"
                              rows={3}
                            />
                          </div>

                          {/* License form for AI suggestion */}
                          <InlineLicenseForm
                            value={suggestionLicenseForm}
                            onChange={setSuggestionLicenseForm}
                            detectedLicense={null}
                            isDetecting={false}
                            confirmed={suggestionLicenseConfirmed}
                            onConfirmChange={setSuggestionLicenseConfirmed}
                          />
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                          <button
                            type="button"
                            onClick={() => setEditingSuggestion(null)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Avbryt
                          </button>
                          <button
                            type="button"
                            onClick={handleAddEditedSuggestion}
                            disabled={!suggestionLicenseConfirmed}
                            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                              suggestionLicenseConfirmed
                                ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            <Plus className="h-4 w-4" />
                            Legg til
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal suggestion view */
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
                            {/* Source status badge */}
                            {suggestion.source_status === 'curated' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium" title="Godkjent kilde fra kurert bibliotek">
                                <Star className="h-3 w-3 fill-green-700" />
                                Godkjent kilde
                              </span>
                            )}
                            {suggestion.source_status === 'ai_verified' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-kf-blue-100 text-kf-blue-700 text-xs rounded-full" title="Nytt KI-forslag med verifisert URL">
                                <ShieldCheck className="h-3 w-3" />
                                KI-forslag (verifisert)
                              </span>
                            )}
                            {suggestion.source_status === 'ai_unverified' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full" title="Nytt KI-forslag - må godkjennes">
                                <AlertCircle className="h-3 w-3" />
                                KI-forslag (ikke godkjent)
                              </span>
                            )}
                            {/* URL status badges */}
                            {suggestion.url_modified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-kf-orange-100 text-kf-orange-700 text-xs rounded-full" title="URL erstattet med hovedside">
                                <Link className="h-3 w-3" />
                                Hovedside
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{suggestion.summary}</p>

                          {/* Show original URL if modified */}
                          {suggestion.url_modified && suggestion.original_url && (
                            <p className="text-xs text-amber-600 mt-1">
                              Original URL ikke tilgjengelig: <span className="font-mono">{suggestion.original_url}</span>
                            </p>
                          )}

                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                            <span className="capitalize">{suggestion.literature_type}</span>
                            <span>Relevans: {suggestion.relevance_score}%</span>
                            {suggestion.url && (
                              <a
                                href={suggestion.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-kf-primary hover:underline flex items-center gap-1"
                              >
                                Se kilde
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>

                        <div className="ml-4 flex flex-col gap-2">
                          {/* Edit before adding */}
                          <button
                            type="button"
                            onClick={() => handleEditSuggestion(suggestion)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm whitespace-nowrap flex items-center gap-2"
                            title="Rediger før du legger til"
                          >
                            <Pencil className="h-4 w-4" />
                            Rediger
                          </button>

                          {/* Curated source - just add */}
                          {suggestion.source_status === 'curated' && (
                            <button
                              type="button"
                              onClick={() => handleAcceptSuggestion(suggestion)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm whitespace-nowrap flex items-center gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Legg til direkte
                            </button>
                          )}

                          {/* AI suggestion - approve and add or just add */}
                          {suggestion.source_status !== 'curated' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApproveAndAddToCurated(suggestion)}
                                className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 text-sm whitespace-nowrap flex items-center gap-2"
                                title="Legg til i studieplanen og send forespørsel om kurering til studieforbundet"
                              >
                                <ThumbsUp className="h-4 w-4" />
                                Legg til & be om kurering
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAcceptSuggestion(suggestion)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm whitespace-nowrap"
                              >
                                Legg til direkte
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowSuggestionsModal(false);
                  setEditingSuggestion(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Literature Modal */}
      {showEditModal && editingLit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4">Rediger litteratur</h3>

            <div className="space-y-4">
              {/* Basic info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tittel *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forfatter
                  </label>
                  <input
                    type="text"
                    value={editForm.author}
                    onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Ola Nordmann"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgiver
                  </label>
                  <input
                    type="text"
                    value={editForm.publisher}
                    onChange={(e) => setEditForm({ ...editForm, publisher: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. Universitetsforlaget"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utgivelsesår
                  </label>
                  <input
                    type="number"
                    value={editForm.publication_year}
                    onChange={(e) => setEditForm({ ...editForm, publication_year: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="F.eks. 2024"
                    min="1900"
                    max="2100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={editForm.isbn}
                    onChange={(e) => setEditForm({ ...editForm, isbn: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="978-82-..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DOI
                  </label>
                  <input
                    type="text"
                    value={editForm.doi}
                    onChange={(e) => setEditForm({ ...editForm, doi: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    placeholder="10.1000/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL *
                </label>
                <input
                  type="url"
                  value={editForm.original_url}
                  onChange={(e) => setEditForm({ ...editForm, original_url: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={editForm.literature_type}
                  onChange={(e) => setEditForm({ ...editForm, literature_type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                >
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="webpage">Nettside</option>
                  <option value="book">Bok</option>
                  <option value="article">Artikkel</option>
                  <option value="video">Video</option>
                  <option value="manual">Manual/Instruksjon</option>
                  <option value="other">Annet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Archive option (task #387: gjør inline-status tydelig for shared/collection) */}
              {(() => {
                const isCollection = /^ndla-(subject|topic)-/.test(editingLit.external_id || '');
                const alreadyArchived = ['downloaded','auto_snapshot','user_uploaded'].includes(editingLit.snapshot_status || editingLit.download_status || '');
                const canSnapshot = ['free','open_access'].includes(editingLit.license_category || '') || !editingLit.license_category;
                return (
                  <div className="bg-gray-50 rounded-lg p-4">
                    {isCollection ? (
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-kf-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-gray-900">Samleside — kan ikke arkiveres</span>
                          <p className="text-sm text-gray-600 mt-1">
                            Dette er et NDLA-fag/-emne som fungerer som en indeks over enkeltartikler.
                            Åpne samlesiden og velg spesifikke artikler du vil arkivere.
                          </p>
                        </div>
                      </div>
                    ) : alreadyArchived ? (
                      <p className="text-sm text-green-600 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Allerede arkivert lokalt {editingLit.local_file_size ? `(${Math.round(editingLit.local_file_size / 1024)} KB)` : ''}
                      </p>
                    ) : (
                      <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.archive_locally}
                            disabled={!canSnapshot}
                            onChange={(e) => setEditForm({ ...editForm, archive_locally: e.target.checked })}
                            className="w-5 h-5 rounded border-gray-300 text-kf-primary focus:ring-kf-primary disabled:opacity-50"
                          />
                          <div>
                            <span className={`font-medium ${canSnapshot ? 'text-gray-900' : 'text-gray-400'}`}>Arkiver lokalt</span>
                            <p className="text-sm text-gray-500">
                              {canSnapshot
                                ? 'Last ned og arkiver innholdet i vår lokale arkivmappe. Lagres når du klikker "Lagre".'
                                : `Lisens (${editingLit.license_label || editingLit.license_category}) tillater ikke lokal arkivering.`}
                            </p>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* License section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-kf-blue-600" />
                    Lisensinformasjon
                  </h4>
                  {/* Task #387: vis lisens fra enten license_type eller license_label/category */}
                  {(editingLit.license_type || editingLit.license_label || editingLit.license_category) && !showLicenseInEdit && (
                    <span className="text-sm text-gray-500">
                      Nåværende: {editingLit.license_label
                                  || (editingLit.license_type === 'public_domain' ? 'Offentlig domene' :
                                      editingLit.license_type === 'creative_commons' ? 'Creative Commons' :
                                      editingLit.license_type === 'open_access' ? 'Åpen tilgang' :
                                      editingLit.license_type)
                                  || (editingLit.license_category === 'free' ? 'Fri (åpen)' :
                                      editingLit.license_category === 'open_access' ? 'Åpen tilgang' :
                                      editingLit.license_category === 'bokhylla' ? 'Bokhylla (NB)' :
                                      editingLit.license_category === 'copyrighted' ? 'Opphavsrettsbeskyttet' :
                                      editingLit.license_category === 'commercial' ? 'Kommersiell' :
                                      editingLit.license_category)}
                    </span>
                  )}
                </div>

                {!showLicenseInEdit ? (
                  <button
                    type="button"
                    onClick={() => setShowLicenseInEdit(true)}
                    className="px-4 py-2 bg-kf-blue-50 text-kf-blue-700 rounded-lg hover:bg-kf-blue-100 text-sm flex items-center gap-2"
                  >
                    <Pencil className="h-4 w-4" />
                    {editingLit.license_type ? 'Endre lisensinformasjon' : 'Legg til lisensinformasjon'}
                  </button>
                ) : (
                  <div className="bg-kf-blue-50 rounded-lg p-4">
                    <InlineLicenseForm
                      value={editLicenseForm}
                      onChange={setEditLicenseForm}
                      detectedLicense={null}
                      isDetecting={false}
                      confirmed={editLicenseConfirmed}
                      onConfirmChange={setEditLicenseConfirmed}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowLicenseInEdit(false);
                        setEditLicenseConfirmed(false);
                      }}
                      className="mt-3 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Avbryt lisensredigering
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingLit(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary"
              >
                Lagre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Correction Modal */}
      {showUrlCorrectionModal && urlCorrectionLit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Search className="h-6 w-6 text-amber-600" />
              Finn alternativ URL
            </h3>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Kilde:</span> {urlCorrectionLit.title}
              </p>
              <p className="text-sm text-gray-500 mt-1 font-mono break-all">
                {urlCorrectionLit.original_url}
              </p>
            </div>

            {loadingUrlSuggestions ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600 mx-auto mb-4" />
                <p className="text-gray-600">Søker etter alternative URLer...</p>
                <p className="text-sm text-gray-500 mt-1">Sjekker Wayback Machine og søker i nettstedets dokumenter</p>
              </div>
            ) : urlSuggestions.length === 0 ? (
              <div className="py-8">
                <div className="text-center mb-6">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Ingen alternative URLer funnet automatisk</p>
                  <p className="text-sm text-gray-500 mt-2">Systemet søkte i Wayback Machine og nettstedets dokumentsider.</p>
                </div>

                {/* Manual URL input as fallback */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 mb-3">
                    Lim inn URL manuelt hvis du har funnet dokumentet:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const input = e.target as HTMLInputElement;
                          if (input.value) {
                            handleApplyUrlCorrection(input.value, 'manual');
                          }
                        }
                      }}
                      id="manual-url-input"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('manual-url-input') as HTMLInputElement;
                        if (input?.value) {
                          handleApplyUrlCorrection(input.value, 'manual');
                        }
                      }}
                      disabled={applyingUrl !== null}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      {applyingUrl ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Bruk URL
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Velg en alternativ URL for å erstatte den ødelagte lenken:
                </p>
                {urlSuggestions.map((suggestion, index) => (
                  <div key={index} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {suggestion.method === 'wayback' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-kf-blue-100 text-kf-blue-700 text-xs rounded-full font-medium">
                              <Clock className="h-3 w-3" />
                              Wayback Machine
                            </span>
                          )}
                          {suggestion.method === 'ai_search' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                              <Sparkles className="h-3 w-3" />
                              KI-søk
                            </span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            suggestion.status === 'valid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {suggestion.status === 'valid' ? 'Tilgjengelig' : 'Ikke verifisert'}
                          </span>
                          <span className="text-sm text-gray-500">
                            Konfidens: {suggestion.confidence}%
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 mt-2">{suggestion.description}</p>

                        <a
                          href={suggestion.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-kf-primary hover:underline mt-2 inline-flex items-center gap-1 break-all"
                        >
                          {suggestion.url}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>

                        {suggestion.timestamp && (
                          <p className="text-xs text-gray-500 mt-1">
                            Arkivert: {suggestion.timestamp.substring(0, 4)}-
                            {suggestion.timestamp.substring(4, 6)}-
                            {suggestion.timestamp.substring(6, 8)}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleApplyUrlCorrection(suggestion.url, suggestion.method)}
                        disabled={applyingUrl !== null}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
                      >
                        {applyingUrl === suggestion.url ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Oppdaterer...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            Bruk denne
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowUrlCorrectionModal(false);
                  setUrlCorrectionLit(null);
                  setUrlSuggestions([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Browser Modal */}
      {showLibraryBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Library className="h-6 w-6 text-indigo-600" />
                Velg fra delt bibliotek
              </h3>
              <button
                type="button"
                onClick={() => setShowLibraryBrowser(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <SharedLiteratureBrowser
                studyPlanId={studyPlanId}
                mode="select"
                onSelect={handleLibrarySelect}
              />
            </div>
          </div>
        </div>
      )}

      {/* Task #349-360 (2026-07-07): ny litteratur-oppdagelses-modal.
          NB: onLiteratureAdded SKAL IKKE trigge loadLiterature — den setter
          loading=true som unmounter hele LiteratureManager (inkl. modal),
          og modal-en mister suggestions-state. Refresh av listen skjer i
          onClose istedet. */}
      {showDiscoveryModal && studyPlanId && (
        <LiteratureDiscoveryModal
          studyPlanId={studyPlanId}
          isOpen={showDiscoveryModal}
          onClose={() => {
            setShowDiscoveryModal(false);
            loadLiterature();
          }}
          onLiteratureAdded={(opts) => {
            // Task #383: propagér ny updated_at til StudyPlanForm så
            // dbUpdatedAt-state syncs og neste save ikke gir 409.
            if (onLiteratureChange) onLiteratureChange(opts);
          }}
        />
      )}

      {/* Fase 4G: Bla og velg-modal for NDLA-samlesider */}
      {browsingCollectionLinkId && (
        <CollectionBrowserModal
          linkId={browsingCollectionLinkId}
          isOpen={!!browsingCollectionLinkId}
          onClose={() => {
            setBrowsingCollectionLinkId(null);
            loadLiterature();
          }}
          onArticlesAdded={(opts) => {
            if (opts.count > 0) {
              setSuccess(`${opts.count} artikler lagt til fra samleside`);
              if (onLiteratureChange) onLiteratureChange({ updated_at: opts.updated_at });
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Banner som vises på en kobling når delt litteratur har fått ny versjon.
 * Tilbyr: Vis hva som er endret · Oppgrader · Behold (skjul varsel)
 * Ikke-tvingende — eksisterende versjon fortsetter å fungere via versjon-snapshot.
 */
function NewVersionBanner({
  sharedLiteratureId,
  linkId,
  currentVersion,
  latestVersion,
  onUpgraded
}: {
  sharedLiteratureId: string;
  linkId: string;
  currentVersion: number;
  latestVersion: number;
  onUpgraded: () => void;
}) {
  const [showDiff, setShowDiff] = useState(false);
  const [diff, setDiff] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { confirm } = useConfirm();

  if (dismissed) return null;

  const loadDiff = async () => {
    setLoading(true);
    try {
      const res = await authenticatedFetch(
        `/api/shared-literature/${sharedLiteratureId}/version-diff?from=${currentVersion}&to=${latestVersion}`
      );
      const data = await res.json();
      if (data.success) setDiff(data);
    } finally { setLoading(false); }
  };

  const upgrade = async () => {
    const ok = await confirm({
      title: `Oppgrader til versjon ${latestVersion}?`,
      message: 'Din kobling vil peke på ny versjon. Dette påvirker ikke andre studieplaner.',
      variant: 'info',
      confirmLabel: `Oppgrader til v${latestVersion}`,
    });
    if (!ok) return;
    setUpgrading(true);
    try {
      const res = await authenticatedFetch(`/api/shared-literature/links/${linkId}/upgrade`, { method: 'PATCH' });
      const data = await res.json();
      if (res.ok && data.success) onUpgraded();
      else alert(data.error || 'Oppgradering feilet');
    } finally { setUpgrading(false); }
  };

  const fieldLabels: Record<string, string> = {
    title: 'Tittel', original_url: 'URL', isbn: 'ISBN', doi: 'DOI',
    author: 'Forfatter', publisher: 'Utgiver', publication_year: 'Utgivelsesår',
    language: 'Språk', description: 'Beskrivelse', literature_type: 'Type',
    local_file_path: 'Lokal fil', download_status: 'Status'
  };

  return (
    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-amber-900">
            <strong>En nyere versjon (v{latestVersion}) er tilgjengelig.</strong> Din kobling
            bruker fortsatt v{currentVersion}.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setShowDiff(true); if (!diff) loadDiff(); }}
              className="text-xs px-2 py-1 border border-amber-300 text-amber-900 rounded hover:bg-amber-100"
            >
              Vis hva som er endret
            </button>
            <button
              type="button"
              onClick={upgrade}
              disabled={upgrading}
              className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            >
              {upgrading ? 'Oppgraderer…' : `Oppgrader til v${latestVersion}`}
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="text-xs px-2 py-1 text-amber-800 hover:underline"
              title="Behold v{currentVersion} og skjul varsel for denne økten"
            >
              Behold v{currentVersion}
            </button>
          </div>
        </div>
      </div>

      {showDiff && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDiff(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Endringer fra v{currentVersion} til v{latestVersion}
              </h3>
            </div>
            <div className="p-5">
              {loading || !diff ? (
                <p className="text-sm text-gray-500">Laster…</p>
              ) : diff.changes.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Ingen endrede felt registrert.</p>
              ) : (
                <div className="space-y-3">
                  {diff.to.summary && (
                    <p className="text-sm bg-gray-50 border border-gray-200 rounded p-2 italic">
                      {diff.to.summary}
                      {diff.to.changed_by && <span className="text-gray-500"> — {diff.to.changed_by}</span>}
                    </p>
                  )}
                  {diff.changes.map((c: any, i: number) => (
                    <div key={i} className="border border-gray-200 rounded">
                      <p className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-700 border-b border-gray-200">
                        {fieldLabels[c.field] || c.field}
                      </p>
                      <div className="p-3 space-y-2 text-sm">
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Før (v{currentVersion}):</span>
                          <p className="text-gray-700 break-words">{c.before == null ? <em className="text-gray-400">tom</em> : String(c.before)}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wide">Etter (v{latestVersion}):</span>
                          <p className="text-gray-900 break-words">{c.after == null ? <em className="text-gray-400">tom</em> : String(c.after)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDiff(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
